import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { GetGroups, GetGroupById, GetGroupChildAccounts, GetGroupNames } from '../../services/backendServices';
import { STORAGE_KEYS } from '../../Util/key_helper';
// Create a context for Group data
const GroupContext = createContext();

/**
 * GroupContextProvider: A provider component to manage and supply group-related data and actions.
 */export const GroupContextProvider = ({ children }) => {
    const [groupData, setGroupData] = useState([]); // state to store group data 
    const [isGroupDataLoading, setIsGroupDataLoading] = useState(false); // State to track loading status for group data 
    const [isGroupDataFetched, setIsGroupDataFetched] = useState(false); // state to track group data has been fetched
    const [groupNameOption, setGroupNameOption] = useState([]);  // State to store group name options for dropdowns or selection
    const [groupDetails, setGroupDetails] = useState(); // state to store group details
    const [isGroupDetailsFetched, setIsGroupDetailsFetched] = useState(false); // state to track if group details has been fetched
    const [isGroupDetailsLoading, setIsGroupDetailsLoading] = useState(false); // state to track the loading state of group details
    const [groupChildAccountsData, setGroupChildAccountsData] = useState([]); // state to store group child account data
    const [isgroupChildAccountLoading, setIsGroupChildAccountLoading] = useState(false); // State to track loading status for group child account data
    const [isRefetchGroupChildaccountData, setIsRefetchGroupChildAccountData] = useState(false); // state to refetch group child account data 
    /**
      * Function Fetches group details by ID.
      * @param {Int} groupId - ID of the group to fetch
      */
    // Add this useEffect to watch groupData changes
    useEffect(() => {
        if (groupData && groupData.length > 0) {
            localStorage.setItem(STORAGE_KEYS.GROUP_COUNT_KEY(), groupData.length.toString());
        } else {
            // Optional: clear or set 0 if no groups
            localStorage.setItem(STORAGE_KEYS.GROUP_COUNT_KEY(), '1');
        }
    }, [groupData]);
    const fetchGroupById = async (groupId) => {
        setIsGroupDetailsLoading(true)
        try {
            const res = await GetGroupById({ group_id: parseInt(groupId) });
            if (res.status) {
                setGroupDetails(res.data);
                setIsGroupDetailsFetched(true);
            }
        } catch (e) {
            console.error("Error fetching Group:", e);
        } finally {
            setIsGroupDetailsLoading(false)
        }
    };
    /**
     * Function to fetches child accounts of a group by group ID.
     * @param {number} groupId - ID of the group
     */
    const fetchGroupChildAccountsData = async (groupId) => {
        setIsGroupChildAccountLoading(true);
        try {
            const res = await GetGroupChildAccounts({ group_id: parseInt(groupId) });
            if (res.status) {
                setGroupChildAccountsData(res.data);
                setIsRefetchGroupChildAccountData(false);
            }
        } catch (e) {
            console.error("Error fetching Group Child Account:", e);
        } finally {
            setIsGroupChildAccountLoading(false);
        }
    };

    // Function  to fetch group data 
    const fetchedGroupData = async () => {
        setIsGroupDataLoading(true)
        try {
            setIsGroupDataLoading(true); // Start loading
            const res = await GetGroups();
            if (res.status) {
                setGroupData(res.data);
                setIsGroupDataFetched(true); // Mark data as fetched
            }
        } catch (e) {
            console.error("Error fetching Group:", e);
        } finally {
            setIsGroupDataLoading(false); // End loading
        }
    };

    // Function the Fetches all group  namesto be used for dropdown selections
    const fetchGroupNames = async () => {
        try {
            const res = await GetGroupNames();
            if (res.status) {
                const groupOptions = res.data.map((account) => ({
                    value: account.group_id,
                    label: account.group_name,
                }));
                setGroupNameOption(groupOptions);
            }
        } catch (error) {
            console.error("Error fetching group names:", error);
        }
    };

    /**
   * Updates specific fields of a group by group ID.
   * @param {number} group_id - ID of the group
   * @param {Object} updatedFields - Fields to update
   */
    const updateGroupField = (group_id, updatedFields) => {
        setGroupData((prevData) =>
            prevData.map((item) =>
                item.group_id === group_id ? { ...item, ...updatedFields } : item
            )
        );
    };
    const updateGroupCount = (group_id, is_add_raw) => {

        setGroupData((prevData) => {
            return prevData.map((item) => {
                if (item.group_id === group_id) {
                    const currentCount = Number(item?.group_broker_count) || 0;
                    const updatedCount = is_add_raw ? currentCount + 1 : Math.max(0, currentCount - 1);
                    return {
                        ...item,
                        group_broker_count: updatedCount,
                    };
                }
                return item;
            });
        });

        setGroupDetails((prevDetails) => {
            if (prevDetails?.group_id === group_id) {
                const currentCount = Number(prevDetails?.group_broker_count) || 0;
                const updatedCount = is_add_raw ? currentCount + 1 : Math.max(0, currentCount - 1);
                return {
                    ...prevDetails,
                    group_broker_count: updatedCount,
                };
            }
            return prevDetails;
        });
    };

    /**
   * Updates a specific group's full data by group ID.
   * @param {Object} updatedData - Updated group data
   */
    const updateGroupData = (updatedData) => {
        setGroupData((prevData) =>
            prevData.map((item) =>
                item.group_id === updatedData.group_id ? { ...item, ...updatedData } : item
            )
        );
    };

    /**
     * Removes a group by group ID.
     * @param {number} group_id - ID of the group to remove
     */
    const removeGroupData = (group_id) => {
        setGroupData((prevData) => prevData.filter((item) => item.group_id !== group_id));
    };

    /**
    * Updates specific fields of group details if group ID matches.
    * @param {number} group_id - ID of the group
    * @param {Object} updatedFields - Fields to update
    */
    const updateGroupDetails = (group_id, updatedFields) => {
        setGroupDetails((prevDetails) => {
            if (prevDetails?.group_id === group_id) {
                return {
                    ...prevDetails,
                    ...updatedFields,
                };
            }
            return prevDetails;
        });
    };
    /**
      * Adds a new child account data entry.
      * @param {Object} newData - New child account data
      */
    const addGroupChildAccountData = (newData) => {
        setGroupChildAccountsData(prevData => [...prevData, newData]);

    }

    /**
     * Updates a specific child account's full data by broker ID.
     * @param {Object} updatedData - Updated child account data
     */
    const updateGroupChildAccountData = (updatedData) => {
        setGroupChildAccountsData((prevData) =>
            prevData.map((item) =>
                item.group_broker_id === updatedData.grp_broker_id ? { ...item, ...updatedData } : item
            )
        );
    };
    /**
       * Updates specific fields of a child account by broker ID.
       * @param {number} group_broker_id - Broker ID of the child account
       * @param {Object} updatedFields - Fields to update
       */
    const updateGroupChildAccountField = (group_broker_id, updatedFields) => {
        setGroupChildAccountsData((prevData) =>
            prevData.map((item) =>
                item.group_broker_id === group_broker_id ? { ...item, ...updatedFields } : item
            )
        );
    };
    /**
     * Updates specific fields of a child account by broker ID.
     * @param {number} broker_id - Broker ID of the child account
     * @param {Object} updatedFields - Fields to update
     */
    const updateGroupChildAccountFieldWithBrokerId = (broker_id, updatedFields) => {
        setGroupChildAccountsData((prevData) =>
            prevData.map((item) =>
                item.broker_id === broker_id ? { ...item, ...updatedFields } : item
            )
        );
    };

    /**
      * Updates an existing account's full data by broker_id
      * @param {Object} updatedData - Updated account data
      */
    const updateGroupChildAccountInfo = (broker_id, updatedAccountInfo) => {
        setGroupChildAccountsData((prevData) =>
            prevData.map((item) =>
                item.broker_id === broker_id
                    ? {
                        ...item,
                        account_info: {
                            ...item.account_info,
                            ...updatedAccountInfo
                        }
                    }
                    : item
            )
        );
    };
    /**
  * Deletes a child account from the list by broker ID.
  * @param {number} group_broker_id - Broker ID of the account to delete
  */
    const removeChildAccountData = (group_broker_id) => {
        setGroupChildAccountsData((prevData) =>
            prevData.filter((item) => item.group_broker_id !== group_broker_id)
        );
    };

    /**
   * Adds a new group entry to the group data.
   * @param {Object} newData - Group data to add
   */
    const addGroupData = (newData) => {
        // Initially Count As Zero 
        const updatedData = { ...newData, group_broker_count: 0, pending_count: 0, filled_count: 0, canceled_count: 0, failed_count: 0, sub_user_count: 0, strategy_subscriptions_count: 0 };
        // Update the state with the new data
        setGroupData(prevData => [...prevData, updatedData]);
    };
    // Function To handle refresh Group Data 
    const handelRefreshGroupData = async () => {
        try {
            await fetchedGroupData();
            toast.success("Data refreshed successfully!");
        } catch (error) {
            toast.error("Error refreshing data. Please try again.");
        }
    };
    /**
    * Refreshes child account data of a group.
    * @param {number} group_id - ID of the group
    */
    const handleRefreshGroupChildAccountData = async (group_id) => {
        try {
            await fetchGroupChildAccountsData(group_id);
            toast.success("Data refreshed successfully!");
        } catch (error) {
            toast.error("Error refreshing data. Please try again.");
        }
    };
    /**
    * Refreshes group details by group ID.
    * @param {number} group_id - ID of the group
    */
    const handleRefreshGroupByIdData = async (group_id) => {
        try {
            await fetchGroupById(group_id);
            toast.success("Data refreshed successfully!");
        } catch (error) {
            toast.error("Error refreshing data. Please try again.");
        }
    };
    //  Resets fetched status flags in the GroupContext.
    const resetGroupContext = () => {
        setIsGroupDataFetched(false);
        setIsGroupDetailsFetched(false);
    };
    return (
        // The GroupContext.Provider component provides state and functions to the children components
        <GroupContext.Provider value={{
            groupData, // Current group data
            isGroupDataLoading, // Loading state of group data
            isGroupDataFetched, // Fetched status of group data
            setIsGroupDataFetched, // Function to manually update the fetched status
            fetchedGroupData, // Function to fetch the group data
            fetchGroupNames, // Function to fetch group name options
            groupNameOption, // Dropdown options for group names
            removeGroupData, // Function to remove a group's data
            updateGroupField, // Function to update a specific field in group data
            updateGroupData, // Function to update overall group data
            fetchGroupById, // Function to fetch group details by ID
            groupDetails, // Current group details
            isGroupDetailsFetched, // Fetched status of group details
            setIsGroupDetailsFetched, // Function to manually update the fetched status of group details
            isGroupDetailsLoading, //loading state of group details
            setGroupDetails, // Function to set group details
            updateGroupDetails, // Function to update specific fields in group details
            fetchGroupChildAccountsData, // Function to fetch group child account data
            groupChildAccountsData, // Current group child account data
            isgroupChildAccountLoading, // Loading status of group child account data
            setGroupChildAccountsData, // Function to set/update group child account data
            addGroupChildAccountData, // Function to add a new group child account
            updateGroupChildAccountData, // Function to update group child account data
            removeChildAccountData, // Function to remove a group child account
            updateGroupChildAccountField, // Function to update a specific field in a group child account
            addGroupData, // Function to add a new group
            handelRefreshGroupData, // Function to refresh group data
            handleRefreshGroupChildAccountData, // Function to refresh group child account data
            handleRefreshGroupByIdData, // Function to refresh group data by ID
            resetGroupContext, // Function to reset the entire group context state
            updateGroupCount, // Function to update the count of brokers in a group
            updateGroupChildAccountFieldWithBrokerId,  // Function to Update Group child Account Data usimng broker id 
            isRefetchGroupChildaccountData,
            setIsRefetchGroupChildAccountData, updateGroupChildAccountInfo
        }}>
            {children}
        </GroupContext.Provider>
    );
};

// Create a custom hook to use the GroupDataContext
export const useGroupContext = () => {
    return useContext(GroupContext);
};
